/** @format — When agent messages should show AG-UI blocks vs text only */

import { filterKnownUiComponents, normalizeAguiComponents } from "./normalizeComponents";
import { hasKnownAguiComponents } from "./aguiMessageUtils";
import {
  isListShipmentsIntent,
  isTrackLocateIntent,
  isTrackingIntent,
} from "./enrichTrackingMapUi";

const CASUAL_AGENT_RE =
  /^(alright|okay|ok|thanks|thank you|thank u|got it|sounds good|perfect|great|cool|no problem|you'?re welcome|happy to help|my pleasure|anytime|sure thing|will do|noted)[\s!.,?]*$/i;

const HEAVY_UI_BLOCKS = new Set(["shipment.list", "tracking.map", "tracking.summary"]);

/** Map AG-UI block names to user-intent patterns that justify showing them again */
const BLOCK_INTENT_RE: Record<string, RegExp> = {
  "port.recommendation":
    /\b(port|cheapest|ship|ocean|vessel|lagos|nigeria|recommend|departure port)\b/i,
  "smart.suggestion":
    /\b(port|cheapest|ship|ocean|suggest|recommend|departure port)\b/i,
  "trucking.quote": /\b(truck|trucking|pickup|haul|terminal|nearest)\b/i,
  "shipment.list": /\b(shipment|list|my shipments)\b/i,
  "tracking.map": /\b(track|where|locate|map|status)\b/i,
  "tracking.summary": /\b(track|where|status|shipment)\b/i,
};

const FRUSTRATED_USER_RE =
  /\b(why (are you|did you) send|stop sending|don't send|didn't ask for|not what i asked|wrong (card|list))\b/i;

export interface ResolveAgentUiOptions {
  text?: string;
  uiComponents?: object[];
  userPrompt?: string;
  metadata?: Record<string, unknown>;
  /** Card button click / forwarded action — drop stale recommendation UI from session snapshot */
  isActionFollowUp?: boolean;
}

export interface ResolveAgentUiResult {
  uiComponents: object[];
  isAgui: boolean;
}

export function isCasualAgentReply(text?: string): boolean {
  const t = String(text ?? "").trim();
  if (!t) return false;
  if (t.length > 320) return false;
  if (CASUAL_AGENT_RE.test(t)) return true;
  return (
    t.length < 100 &&
    /^(yes|no|sure|done|welcome|hello|hi|hey)\b/i.test(t) &&
    !isTrackingIntent(t)
  );
}

function metadataHasUiPayload(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return true;
  return false;
}

export function hasExplicitPersistedUi(metadata: Record<string, unknown> = {}): boolean {
  if (metadata?.agui_action) return true;
  if (
    metadataHasUiPayload(metadata.ui_components) ||
    metadataHasUiPayload(metadata.uiComponents) ||
    metadataHasUiPayload(metadata.agui_ui_components) ||
    metadataHasUiPayload(metadata.generative_ui) ||
    metadataHasUiPayload(metadata.generativeUi)
  ) {
    return true;
  }
  const agui = metadata.agui;
  if (agui && typeof agui === "object") {
    return metadataHasUiPayload((agui as Record<string, unknown>).ui_components);
  }
  return false;
}

function parseMetadataUiRaw(raw: unknown): object[] {
  if (raw == null) return [];
  let value: unknown = raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
      try {
        value = JSON.parse(trimmed);
      } catch {
        return [];
      }
    } else {
      return [];
    }
  }
  return filterKnownUiComponents(normalizeAguiComponents(value));
}

function uiComponentsFromMetadata(metadata: Record<string, unknown> = {}): object[] {
  const candidates = [
    metadata.ui_components,
    metadata.uiComponents,
    metadata.agui_ui_components,
    metadata.generative_ui,
    metadata.generativeUi,
  ];
  const agui = metadata.agui;
  if (agui && typeof agui === "object") {
    candidates.push((agui as Record<string, unknown>).ui_components);
  }
  for (const raw of candidates) {
    const ui = parseMetadataUiRaw(raw);
    if (ui.length) return ui;
  }
  return [];
}

function userPromptSupportsHeavyUi(userText: string): boolean {
  const t = String(userText ?? "").trim();
  if (!t) return false;
  return isTrackLocateIntent(t) || isListShipmentsIntent(t) || isTrackingIntent(t);
}

function userPromptRequestsBlockType(userPrompt = "", blockName = ""): boolean {
  const re = BLOCK_INTENT_RE[blockName];
  if (!re) return false;
  return re.test(String(userPrompt ?? ""));
}

export function collectAgentUiBlocks(
  message: Record<string, unknown> | null | undefined,
): object[] {
  if (!message || message.role !== "agent") return [];
  const fromMessage = filterKnownUiComponents(
    (message.uiComponents as object[] | undefined) ?? [],
  );
  if (fromMessage.length) return fromMessage;
  return uiComponentsFromMetadata((message.metadata as Record<string, unknown>) ?? {});
}

/**
 * Remove AG-UI blocks carried over from the previous agent turn (session snapshot bleed).
 * Strips by block id when unchanged, and by block name when the user did not ask for that UI again.
 */
export function stripCarriedOverTurnUi(
  uiComponents: object[] = [],
  previousTurnUi: object[] = [],
  userPrompt = "",
): object[] {
  const prev = filterKnownUiComponents(previousTurnUi);
  const current = filterKnownUiComponents(uiComponents);
  if (!prev.length) return current;

  const prevIds = new Set(
    prev.map((block) => (block as { id?: string }).id).filter(Boolean) as string[],
  );
  const prevNames = new Set(prev.map((block) => (block as { name?: string }).name).filter(Boolean));

  return current.filter((block) => {
    const name = (block as { name?: string }).name ?? "";
    const id = (block as { id?: string }).id;
    if (id && prevIds.has(id)) return false;
    if (prevNames.has(name) && !userPromptRequestsBlockType(userPrompt, name)) {
      return false;
    }
    return true;
  });
}

/** @deprecated Use stripCarriedOverTurnUi */
export function stripStaleActionFollowUpUi(uiComponents: object[] = []): object[] {
  return stripCarriedOverTurnUi(uiComponents, [
    { name: "port.recommendation" },
    { name: "smart.suggestion" },
  ]);
}

/** Agent turn that included any AG-UI blocks */
export function agentMessageHasAguiUi(
  message: Record<string, unknown> | null | undefined,
): boolean {
  return collectAgentUiBlocks(message).length > 0;
}

/** @deprecated Use agentMessageHasAguiUi */
export function agentMessageHasRecommendationUi(
  message: Record<string, unknown> | null | undefined,
): boolean {
  return collectAgentUiBlocks(message).some((block) =>
    ["port.recommendation", "smart.suggestion"].includes(
      (block as { name?: string }).name ?? "",
    ),
  );
}

export function stripCarriedOverAgentUi(
  uiComponents: object[] = [],
  previousAgentMessage: Record<string, unknown> | null = null,
  userPrompt = "",
): object[] {
  return stripCarriedOverTurnUi(
    uiComponents,
    collectAgentUiBlocks(previousAgentMessage),
    userPrompt,
  );
}

/**
 * Follow-up after a port pick — either explicit user action text/metadata, or
 * back-to-back agent turns when the API did not persist the selection user row.
 */
function findPreviousAgentMessage(
  messages: Record<string, unknown>[] = [],
  beforeIndex: number,
): Record<string, unknown> | null {
  for (let i = beforeIndex - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === "agent") return messages[i];
  }
  return null;
}

/** Whether this agent turn should drop UI carried over from the previous agent turn */
export function shouldStripCarriedOverUi(
  messages: Record<string, unknown>[] = [],
  agentIndex: number,
): boolean {
  const prevAgent = findPreviousAgentMessage(messages, agentIndex);
  if (!prevAgent || !agentMessageHasAguiUi(prevAgent)) return false;

  const prev = messages[agentIndex - 1];
  if (!prev) return true;

  if (prev.role === "agent") return true;

  if (prev.role === "user") {
    if (isPortCardActionUserMessage((prev.text as string) ?? "", (prev.metadata as Record<string, unknown>) ?? {})) {
      return true;
    }
    if (isLikelyPortSelectionUserMessage(
      (prev.text as string) ?? "",
      (prev.metadata as Record<string, unknown>) ?? {},
      prevAgent,
    )) {
      return true;
    }
    return true;
  }

  return false;
}

/** @deprecated Use shouldStripCarriedOverUi */
export function shouldStripRecommendationFollowUp(
  messages: Record<string, unknown>[] = [],
  agentIndex: number,
): boolean {
  return shouldStripCarriedOverUi(messages, agentIndex);
}

/** User clicked a port card CTA (e.g. "Freeport TX (Grimaldi)") — follow-up should be text-only */
export function isPortCardActionUserMessage(
  text = "",
  metadata: Record<string, unknown> = {},
): boolean {
  if (metadata?.agui_action) return true;
  if (metadata?.system_message_type === "agui_action") return true;

  const t = String(text ?? "").trim();
  if (!t || t.length > 120) return false;
  if (/^Selected:/i.test(t)) return true;
  if (/^Choose /i.test(t)) return true;

  if (
    /\([^)]+\)/.test(t) &&
    !/[?.!]/.test(t) &&
    !/\b(ship|which|cheapest|need|please|track|where)\b/i.test(t)
  ) {
    return t.length <= 80;
  }
  return false;
}

/** Short selection reply immediately after a port recommendation turn */
export function isLikelyPortSelectionUserMessage(
  text = "",
  metadata: Record<string, unknown> = {},
  previousAgentMessage: Record<string, unknown> | null = null,
): boolean {
  if (isPortCardActionUserMessage(text, metadata)) return true;
  if (!agentMessageHasRecommendationUi(previousAgentMessage)) return false;

  const t = String(text ?? "").trim();
  if (!t || t.length > 80 || t.includes("?")) return false;
  if (/\b(ship|track|cheapest|need|please|hello|help|where|which)\b/i.test(t)) {
    return false;
  }
  return true;
}

export function resolveAgentUiForDisplay({
  text,
  uiComponents = [],
  userPrompt = "",
  metadata = {},
  isActionFollowUp = false,
  previousTurnUi = [],
}: ResolveAgentUiOptions & { previousTurnUi?: object[] } = {}): ResolveAgentUiResult {
  let ui = filterKnownUiComponents(uiComponents);
  const agentText = String(text ?? "").trim();
  const persisted = hasExplicitPersistedUi(metadata) && !isActionFollowUp;

  if (!ui.length && persisted) {
    ui = uiComponentsFromMetadata(metadata);
  }

  if (previousTurnUi.length || isActionFollowUp) {
    ui = stripCarriedOverTurnUi(ui, previousTurnUi, userPrompt);
  }

  if (!ui.length) {
    return { uiComponents: [], isAgui: false };
  }

  if ((metadata?.agui_action || persisted) && !isActionFollowUp) {
    return { uiComponents: ui, isAgui: hasKnownAguiComponents(ui) };
  }

  if (
    isCasualAgentReply(agentText) ||
    isCasualAgentReply(userPrompt) ||
    FRUSTRATED_USER_RE.test(userPrompt)
  ) {
    return { uiComponents: [], isAgui: false };
  }

  const onlyHeavy = ui.every((b) => HEAVY_UI_BLOCKS.has(b.name));
  if (onlyHeavy && !userPromptSupportsHeavyUi(userPrompt) && !userPromptSupportsHeavyUi(agentText)) {
    return { uiComponents: [], isAgui: false };
  }

  if (!hasExplicitPersistedUi(metadata) && onlyHeavy && agentText.length > 0 && agentText.length < 400) {
    if (!userPromptSupportsHeavyUi(userPrompt)) {
      return { uiComponents: [], isAgui: false };
    }
  }

  return { uiComponents: ui, isAgui: hasKnownAguiComponents(ui) };
}

export function sanitizeAgentViewModel(
  message: Record<string, unknown>,
  userPrompt = "",
  userMetadata: Record<string, unknown> = {},
  options: {
    isActionFollowUp?: boolean;
    previousAgentMessage?: Record<string, unknown> | null;
  } = {},
): Record<string, unknown> {
  if (!message || message.role !== "agent") return message;
  const metadata = (message.metadata as Record<string, unknown>) ?? {};
  const isActionFollowUp =
    options.isActionFollowUp ||
    isPortCardActionUserMessage(userPrompt, userMetadata);
  const previousTurnUi = collectAgentUiBlocks(options.previousAgentMessage ?? null);
  const shouldStrip =
    isActionFollowUp ||
    shouldStripCarriedOverUiFromContext(userPrompt, userMetadata, options.previousAgentMessage);

  if (
    !shouldStrip &&
    hasExplicitPersistedUi(metadata) &&
    hasKnownAguiComponents(message.uiComponents as object[] | undefined)
  ) {
    return { ...message, isAgui: true };
  }

  const { uiComponents, isAgui } = resolveAgentUiForDisplay({
    text: message.text as string | undefined,
    uiComponents: message.uiComponents as object[] | undefined,
    userPrompt,
    metadata,
    isActionFollowUp,
    previousTurnUi: shouldStrip ? previousTurnUi : [],
  });
  return { ...message, uiComponents, isAgui };
}

function shouldStripCarriedOverUiFromContext(
  userPrompt = "",
  userMetadata: Record<string, unknown> = {},
  previousAgentMessage: Record<string, unknown> | null = null,
): boolean {
  if (!agentMessageHasAguiUi(previousAgentMessage)) return false;
  if (isPortCardActionUserMessage(userPrompt, userMetadata)) return true;
  return collectAgentUiBlocks(previousAgentMessage).length > 0;
}

export function sanitizeEnrichedChatMessages(
  messages: Record<string, unknown>[] = [],
): Record<string, unknown>[] {
  let lastUserText = "";
  let lastUserMetadata: Record<string, unknown> = {};
  return messages.map((m, index) => {
    if (m.role === "user") {
      lastUserText = (m.text as string) ?? "";
      lastUserMetadata = (m.metadata as Record<string, unknown>) ?? {};
      return m;
    }
    if (m.role === "agent") {
      const previousAgent = findPreviousAgentMessage(messages, index);
      const isActionFollowUp = shouldStripCarriedOverUi(messages, index);
      return sanitizeAgentViewModel(m, lastUserText, lastUserMetadata, {
        isActionFollowUp,
        previousAgentMessage: previousAgent,
      });
    }
    return m;
  });
}
